import { useState, useRef, useCallback, useEffect } from "react"

const MIN_W = 420
const MIN_H = 240
const TITLEBAR_H = 28
// Never let the window be dragged so far that its title bar is unreachable.
const KEEP_VISIBLE = 90

/**
 * Free positioning for the shell's window: drag by the title bar, resize from
 * any edge or corner.
 *
 * Geometry starts null, meaning "let flexbox place it". The first drag or
 * resize seeds it from the window's current box so nothing jumps, and from
 * then on the window is absolutely positioned inside the desk surface.
 *
 * A window can also be handed a box to open at, which is how the cascade
 * works. That matters for more than looks: a window with no geometry is a
 * flex item, and several of those do not overlap, they share the column
 * between them and each one ends up a sliver.
 */
export const useWindowGeometry = (winRef, surfaceRef, initial = null) => {
  // Only read on the first render, which is what we want: after that the box
  // belongs to whoever has been dragging it.
  const [geometry, setGeometry] = useState(initial)
  const [isDragging, setIsDragging] = useState(false)
  const gestureRef = useRef(null)

  const clamp = useCallback(
    g => {
      const surface = surfaceRef.current?.getBoundingClientRect()
      if (!surface) return g

      const w = Math.max(MIN_W, Math.min(g.w, surface.width))
      const h = Math.max(MIN_H, Math.min(g.h, surface.height))

      return {
        w,
        h,
        x: Math.min(
          Math.max(g.x, KEEP_VISIBLE - w),
          surface.width - KEEP_VISIBLE
        ),
        y: Math.min(Math.max(g.y, 0), surface.height - TITLEBAR_H),
      }
    },
    [surfaceRef]
  )

  const begin = useCallback(
    (mode, e) => {
      if (e.button !== 0) return

      const win = winRef.current?.getBoundingClientRect()
      const surface = surfaceRef.current?.getBoundingClientRect()
      if (!win || !surface) return

      const start = {
        x: win.left - surface.left,
        y: win.top - surface.top,
        w: win.width,
        h: win.height,
      }

      setGeometry(clamp(start))
      gestureRef.current = { mode, start, px: e.clientX, py: e.clientY }
      setIsDragging(true)
      e.preventDefault()
    },
    [winRef, surfaceRef, clamp]
  )

  useEffect(() => {
    if (!isDragging) return undefined

    const onMove = e => {
      const gesture = gestureRef.current
      if (!gesture) return

      const { mode, start } = gesture
      const dx = e.clientX - gesture.px
      const dy = e.clientY - gesture.py

      if (mode === "move") {
        setGeometry(clamp({ ...start, x: start.x + dx, y: start.y + dy }))
        return
      }

      let { x, y, w, h } = start
      if (mode.includes("e")) w = start.w + dx
      if (mode.includes("s")) h = start.h + dy
      if (mode.includes("w")) w = start.w - dx
      if (mode.includes("n")) h = start.h - dy

      // Apply the minimum before deriving the origin, otherwise dragging a
      // west or north edge past the limit keeps moving the window.
      w = Math.max(MIN_W, w)
      h = Math.max(MIN_H, h)
      if (mode.includes("w")) x = start.x + (start.w - w)
      if (mode.includes("n")) y = start.y + (start.h - h)

      setGeometry(clamp({ x, y, w, h }))
    }

    const onEnd = () => {
      gestureRef.current = null
      setIsDragging(false)
    }

    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onEnd)
    window.addEventListener("pointercancel", onEnd)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onEnd)
      window.removeEventListener("pointercancel", onEnd)
    }
  }, [isDragging, clamp])

  // A shrinking viewport can strand the window off the surface.
  useEffect(() => {
    const onResize = () => setGeometry(g => (g ? clamp(g) : g))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [clamp])

  const reset = useCallback(() => setGeometry(null), [])

  return { geometry, isDragging, begin, reset }
}

export const RESIZE_HANDLES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"]
